import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { createPortal } from "react-dom";

import { useIsMobile } from "@/hooks/useIsMobile";

/**
 * El panel que cuelga de un botón de la cabecera.
 *
 * ── Por qué deja de ser un cajón ────────────────────────────────────────────
 * El menú de aplicaciones era un cajón de 340 px a altura completa, con velo
 * negro al 35 % y desenfoque detrás. Eso significa que cada vez que alguien
 * quiere cambiar de app, la pantalla entera —el mapa, la lista de unidades, lo
 * que estaba mirando— se apaga. Para una lista de destinos que se consulta en
 * dos segundos, apagar el contexto cuesta más de lo que vale.
 *
 * Un popover anclado sale DEL botón que lo abrió, no tapa nada que importe y
 * conserva la relación entre el control y lo que abrió. En el teléfono no hay
 * sitio para anclar nada, así que ahí sí es una hoja desde abajo —y ahí el velo
 * sí corresponde, porque la hoja ocupa media pantalla.
 *
 * ── Por qué es un componente y no tres copias ───────────────────────────────
 * Lo usan los tres menús de la cabecera —avisos, aplicaciones y cuenta—. Las
 * tres necesitan exactamente lo mismo: portal (si no, el `overflow` de la
 * cabecera lo recorta), reposición al desplazar, cierre con Escape y con un
 * clic fuera, y foco devuelto al botón al cerrar. Escrito tres veces, se
 * arregla una vez y se olvidan dos.
 */

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  /** El botón del que cuelga. Se usa para situarlo y para devolverle el foco. */
  anclaRef?: RefObject<HTMLElement | null>;
  /**
   * Alternativa al `ref` para cuando el botón vive en otro árbol de React.
   *
   * Se resuelve EN EL MOMENTO de colocar el panel, no en un efecto previo. Ésa
   * era la causa del fallo: el padre asignaba la referencia en un `useEffect` y
   * el hijo colocaba el panel en un `useLayoutEffect`, que corre ANTES. En la
   * primera apertura la referencia todavía era `null`, `situar()` se rendía y
   * el panel se quedaba en (0,0), pegado a la esquina.
   *
   * En desarrollo no se veía: `StrictMode` invoca los efectos dos veces y el
   * segundo pase ya encontraba la referencia puesta. Sólo aparecía en el build
   * desplegado, que no los duplica.
   */
  anclaSelector?: string;
  /** Lo que lee quien no ve la pantalla. */
  etiqueta: string;
  ancho?: number;
  children: React.ReactNode;
}

const MARGEN = 8;

export default function PanelAnclado({
  abierto,
  onCerrar,
  anclaRef,
  anclaSelector,
  etiqueta,
  ancho = 320,
  children,
}: Props) {
  const enTelefono = useIsMobile();
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [entrado, setEntrado] = useState(false);
  const refPanel = useRef<HTMLDivElement>(null);

  const obtenerAncla = useCallback(
    () =>
      anclaRef?.current ??
      (anclaSelector ? document.querySelector<HTMLElement>(anclaSelector) : null),
    [anclaRef, anclaSelector],
  );

  const situar = useCallback(() => {
    const ancla = obtenerAncla();
    if (!ancla) {
      /*
        Sin botón del que colgar, el panel se arrima a la esquina superior
        derecha, que es donde viven todos los que lo abren. Antes se quedaba en
        la posición inicial (0,0) —arriba a la izquierda, encima del contenido—
        que no es un sitio, es la ausencia de uno.
      */
      setPos({ top: 64, left: Math.max(MARGEN, window.innerWidth - ancho - MARGEN) });
      return;
    }
    const r = ancla.getBoundingClientRect();
    // Alineado por la derecha del botón, y forzado dentro de la ventana: estos
    // botones viven pegados al borde derecho y sin esto el panel se sale.
    const left = Math.min(
      Math.max(MARGEN, r.right - ancho),
      window.innerWidth - ancho - MARGEN,
    );
    setPos({ top: r.bottom + 8, left });
  }, [obtenerAncla, ancho]);

  useLayoutEffect(() => {
    if (!abierto || enTelefono) return;
    situar();
    const alMover = () => situar();
    window.addEventListener("scroll", alMover, true);
    window.addEventListener("resize", alMover);
    return () => {
      window.removeEventListener("scroll", alMover, true);
      window.removeEventListener("resize", alMover);
    };
  }, [abierto, enTelefono, situar]);

  // La entrada se dispara en el fotograma siguiente: si la clase final ya está
  // puesta en el primer pintado, no hay transición que ver.
  useEffect(() => {
    if (!abierto) {
      setEntrado(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntrado(true));
    return () => cancelAnimationFrame(id);
  }, [abierto]);

  /*
    Al cerrar, el foco vuelve al botón. Sin esto, quien navega con el teclado
    cierra el menú y el foco se queda en la nada: el siguiente Tab empieza
    desde el principio del documento.
  */
  const cerrarYDevolverFoco = useCallback(() => {
    onCerrar();
    obtenerAncla()?.focus();
  }, [onCerrar, obtenerAncla]);

  useEffect(() => {
    if (!abierto) return;
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        cerrarYDevolverFoco();
      }
    };
    window.addEventListener("keydown", alTeclado);
    return () => window.removeEventListener("keydown", alTeclado);
  }, [abierto, cerrarYDevolverFoco]);

  /*
    Cerrar al tocar fuera, sin tapar la pantalla.

    Esto era un botón a pantalla completa, y por eso con un panel abierto el
    primer clic en OTRO botón que abre panel no llegaba nunca: lo recogía el
    velo. Se cerraba el primero y el segundo no se abría, así que había que
    pulsar dos veces.

    Con un escucha en el documento el clic sigue su camino. Se ignora el que
    cae dentro del panel —ahí no se cierra nada— y el que cae sobre cualquier
    botón que abra un panel: ése ya sabe lo que tiene que hacer, y cerrar
    nosotros además lo dejaría abriendo lo que acabábamos de cerrar.
  */
  useEffect(() => {
    if (!abierto) return;
    const alPulsarFuera = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (refPanel.current?.contains(t)) return;
      /*
        Sólo se ignora MI propio botón: ése ya alterna este panel, y cerrar
        además lo dejaría abriendo lo que acabábamos de cerrar. El botón de
        otro panel sí me cierra —si no, se quedarían los dos abiertos, uno
        encima del otro.
      */
      const mio = obtenerAncla();
      if (mio && (mio === t || mio.contains(t))) return;
      onCerrar();
    };
    // En captura: así se decide antes de que nadie detenga la propagación.
    document.addEventListener("pointerdown", alPulsarFuera, true);
    return () => document.removeEventListener("pointerdown", alPulsarFuera, true);
  }, [abierto, onCerrar, obtenerAncla]);

  if (!abierto) return null;

  const contenido = (
    <div
      ref={refPanel}
      role="dialog"
      aria-modal={enTelefono}
      aria-label={etiqueta}
      style={
        enTelefono
          ? undefined
          : { top: pos.top, left: pos.left, width: ancho, maxHeight: "min(72vh, 620px)" }
      }
      className={
        enTelefono
          ? `hud-panel fixed inset-x-0 bottom-0 z-[9999] flex max-h-[82dvh] flex-col rounded-t-xl pb-[env(safe-area-inset-bottom)] transition-transform duration-200 ease-out ${
              entrado ? "translate-y-0" : "translate-y-full"
            }`
          : `hud-panel fixed z-[9999] flex flex-col overflow-hidden rounded-xl transition-[opacity,transform] duration-150 ease-out ${
              entrado ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"
            }`
      }
    >
      {/*
        El asidero de la hoja. No es adorno: en el teléfono es la única pista
        de que esto se cierra arrastrando o tocando fuera.
      */}
      {enTelefono && (
        <span
          aria-hidden="true"
          className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-content-faint/40"
        />
      )}
      {children}
    </div>
  );

  return createPortal(
    <>
      {/*
        En escritorio el fondo NO se oscurece: el popover sale del botón y el
        mapa detrás se sigue viendo y usando. En el teléfono la hoja sí tapa
        media pantalla, y ahí el velo dice qué quedó inactivo.

        No recibe el puntero: cerrar al tocar fuera lo lleva el escucha del
        documento. Cuando esto era un botón a pantalla completa se comía el
        primer clic de cualquier otro botón que abriera panel.
      */}
      {enTelefono && (
        <div
          aria-hidden="true"
          className={`pointer-events-none fixed inset-0 z-[9998] transition-colors duration-200 ${
            entrado ? "bg-black/40" : "bg-transparent"
          }`}
        />
      )}
      {contenido}
    </>,
    document.body,
  );
}
